import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { ArrowLeft, ChevronLeft, ChevronRight, ArrowUpDown, Trash2, GripVertical, AlertCircle, Check, Clock, X, MessageSquare, Building } from 'lucide-react';
import WeatherBadge from '../components/WeatherBadge';
import WeatherDetailModal from '../components/WeatherDetailModal';
import { fetchWeekWeather, getSavedLocation } from '../services/weatherService';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import API_URL from '../config';
import { parseDateSafe, formatDateSafe, formatTimeSafe, getCalendarDayOffset, toCalendarDateKey } from '../utils/dateUtils';

const SortableUserItem = ({ id, user, index, moveUser, isFirst, isLast, colorMatch }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow group ${isDragging ? 'ring-2 ring-indigo-500 shadow-lg opacity-90' : ''}`}
    >
      <div className="flex items-center gap-3">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <GripVertical className="w-5 h-5" />
        </div>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white bg-gradient-to-br ${colorMatch.gradient}`}>
          {(user.full_name || user.email || "?").charAt(0).toUpperCase()}
        </div>
        <span className="font-medium text-gray-700">{user.full_name || user.email}</span>
      </div>
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => moveUser(index, 'up')}
          disabled={isFirst}
          className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
        >
          ↑
        </button>
        <button
          onClick={() => moveUser(index, 'down')}
          disabled={isLast}
          className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
        >
          ↓
        </button>
      </div>
    </div>
  );
};

const SHIFTS_API = `${API_URL}/shifts`;
const USERS_API = `${API_URL}/users`;
const LEAVES_API = `${API_URL}/api/leaves`;
const REQUESTS_API = `${API_URL}/requests`;
const ROOMS_API = `${API_URL}/rooms`;
const TAGS_API = `${API_URL}/tags`;

// ── Leave datetime helpers ────────────────────────────────────────────────
function getLeavePeriod(start_datetime, end_datetime) {
  const startHour = parseDateSafe(start_datetime).getHours();
  const endHour   = parseDateSafe(end_datetime).getHours();
  const sameDay = (toCalendarDateKey(start_datetime) || start_datetime) === (toCalendarDateKey(end_datetime) || end_datetime);
  if (sameDay) {
    if (startHour === 0  && endHour === 12) return 'morning';
    if (startHour === 12 && endHour !== 12) return 'afternoon';
  }
  return 'full';
}

function getLeaveHalves(leave, dateStr) {
  const leaveStartDate = toCalendarDateKey(leave.start_datetime);
  const leaveEndDate = toCalendarDateKey(leave.end_datetime);
  const isSingleDay    = leaveStartDate === leaveEndDate;
  if (isSingleDay && dateStr === leaveStartDate) {
    const p = getLeavePeriod(leave.start_datetime, leave.end_datetime);
    return { isHalfM: p === 'morning', isHalfA: p === 'afternoon' };
  }
  if (dateStr === leaveStartDate) {
    const startHour = parseDateSafe(leave.start_datetime).getHours();
    return { isHalfM: false, isHalfA: startHour >= 12 };
  }
  if (dateStr === leaveEndDate) {
    const endHour = parseDateSafe(leave.end_datetime).getHours();
    return { isHalfM: endHour <= 12, isHalfA: false };
  }
  return { isHalfM: false, isHalfA: false };
}

export default function VueJournee() {
  const { date } = useParams();
  const navigate = useNavigate();
  const { isTeamLeaderOrAbove } = useAuth();

  const token = localStorage.getItem("token");
  const timelineRef = useRef(null);

  // Helper functions for date formatting
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const toLocalISOString = (date) => {
    const pad = (n) => n.toString().padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}:00`;
  };

  const createLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // State
  const [selectedDate, setSelectedDate] = useState(createLocalDate(date));

  // Calculate dates for 5-day view (2 before, current, 2 after)
  const twoDaysBefore = new Date(selectedDate);
  twoDaysBefore.setDate(twoDaysBefore.getDate() - 2);

  const oneDayBefore = new Date(selectedDate);
  oneDayBefore.setDate(oneDayBefore.getDate() - 1);

  const oneDayAfter = new Date(selectedDate);
  oneDayAfter.setDate(oneDayAfter.getDate() + 1);

  const twoDaysAfter = new Date(selectedDate);
  twoDaysAfter.setDate(twoDaysAfter.getDate() + 2);

  // For API fetching, we still use a range
  const previousDate = twoDaysBefore;
  const nextDate = twoDaysAfter;

  // Helper pour vérifier si un shift a lieu dans la plage des 5 jours ouverts
  const isShiftInOpenWindow = (shift) => {
    if (!shift || !shift.start_time || !shift.end_time) return false;

    const openDates = new Set([
      formatDateLocal(twoDaysBefore),
      formatDateLocal(oneDayBefore),
      formatDateLocal(selectedDate),
      formatDateLocal(oneDayAfter),
      formatDateLocal(twoDaysAfter),
    ]);

    const shiftStartDateKey = toCalendarDateKey(shift.start_time);
    const shiftEndDateKey = toCalendarDateKey(shift.end_time);

    if (openDates.has(shiftStartDateKey) || openDates.has(shiftEndDateKey)) {
      return true;
    }

    const wStart = new Date(twoDaysBefore);
    wStart.setHours(0, 0, 0, 0);
    const wEnd = new Date(twoDaysAfter);
    wEnd.setHours(23, 59, 59, 999);

    const shiftStart = parseDateSafe(shift.start_time);
    const shiftEnd = parseDateSafe(shift.end_time);

    return !!(shiftStart && shiftEnd && shiftEnd > wStart && shiftStart <= wEnd);
  };

  const isUserActive = (user) => {
    if (!user) return false;
    if (user.is_active === false || user.is_active === 0 || user.is_active === 'false') {
      return false;
    }
    return true;
  };

  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [requests, setRequests] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState(() => {
    const saved = localStorage.getItem('selectedRoomId');
    return saved ? parseInt(saved) : null;
  });
  const [tags, setTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [dragState, setDragState] = useState(null);
  const dragMetricsRef = useRef({ moved: false, startX: 0 });

  // Shift action modal state
  const [shiftActionModal, setShiftActionModal] = useState({
    show: false,
    shift: null,
    mode: null, // "edit", "delete", "request", "view-request"
    request: null // For viewing pending requests
  });

  // Edit shift state
  const [editShift, setEditShift] = useState({
    user_id: "",
    start_time: "",
    end_time: "",
    no_show: false,
    room_id: "",
    break_duration: 0,
    comment: "",
  });

  // Request modification state
  const [requestModification, setRequestModification] = useState({
    start_time: "",
    end_time: "",
  });

  // User ordering state
  const [usersOrder, setUsersOrder] = useState(() => {
    const saved = localStorage.getItem('usersOrder');
    return saved ? JSON.parse(saved) : [];
  });
  const [sortedUsers, setSortedUsers] = useState([]);
  // Tri local par heure de début (seulement sur VueJournee, non persisté)
  const [sortByTime, setSortByTime] = useState(false);

  // Weather state (Open-Meteo)
  const [weatherData, setWeatherData] = useState({});
  const [weatherLocation, setWeatherLocation] = useState(getSavedLocation);
  const [weatherModalState, setWeatherModalState] = useState({
    show: false,
    dayWeather: null,
  });

  const handleOpenWeatherModal = (dayWeather) => {
    setWeatherModalState({
      show: true,
      dayWeather,
    });
  };

  // Sort employees based on order
  useEffect(() => {
    if (employees.length === 0) {
      setSortedUsers([]);
      return;
    }

    // Filtrer les personnes inactives : elles n'apparaissent QUE si elles ont des shifts dans les 5 jours ouverts
    const visibleEmployees = employees.filter((u) => {
      if (isUserActive(u)) return true; // Actif par défaut ou explicitement
      return shifts.some((shift) => shift.user_id === u.id && isShiftInOpenWindow(shift));
    });

    if (visibleEmployees.length === 0) {
      setSortedUsers([]);
      return;
    }

    // Ordre de base : localStorage ou ordre naturel
    let base;
    if (usersOrder.length > 0) {
      base = [...visibleEmployees].sort((a, b) => {
        const indexA = usersOrder.indexOf(a.id);
        const indexB = usersOrder.indexOf(b.id);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return (a.full_name || '').localeCompare(b.full_name || '') || a.id - b.id;
      });
    } else {
      base = [...visibleEmployees];
    }

    if (sortByTime) {
      // Tri par heure de début du shift sur selectedDate (plus tôt en haut)
      const dateStr = formatDateLocal(selectedDate);
      const getStartTime = (employee) => {
        const dayShift = shifts.find(s =>
          s.user_id === employee.id &&
          toCalendarDateKey(s.start_time) === dateStr
        );
        // Retourner l'heure de début comme string ISO pour comparaison, ou '99:99' si pas de shift
        return dayShift ? (dayShift.start_time || '').slice(11, 16) : '99:99';
      };
      base = [...base].sort((a, b) => getStartTime(a).localeCompare(getStartTime(b)));
    }

    setSortedUsers(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, usersOrder, sortByTime, shifts, selectedDate]);



  // Global mouse event handlers for drag and drop
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (!dragState || !timelineRef.current) return;

      if (dragMetricsRef.current && Math.abs(e.clientX - dragMetricsRef.current.startX) > 5) {
        dragMetricsRef.current.moved = true;
      }

      const rect = timelineRef.current.getBoundingClientRect();
      const currentSlotWidth = rect.width / 48; // one day width / 48 slots

      if (dragState.type === 'move') {
        const deltaPixels = e.clientX - dragState.startX;
        const deltaSlots = Math.round(deltaPixels / currentSlotWidth);

        setDragState(prev => ({
          ...prev,
          currentX: e.clientX,
          deltaSlots: deltaSlots
        }));
      } else if (dragState.type === 'create' || dragState.type === 'resize') {
        // For create/resize, we want slot under mouse
        const relativeX = e.clientX - rect.left;
        let slotIndex = Math.floor(relativeX / currentSlotWidth);
        slotIndex = Math.max(0, Math.min(slotIndex, 239)); // Clamp to 5 days

        if (dragState.type === 'create' && slotIndex !== dragState.endSlot) {
          setDragState(prev => ({ ...prev, endSlot: slotIndex }));
        } else if (dragState.type === 'resize' && slotIndex !== dragState.currentSlot) {
          setDragState(prev => ({ ...prev, currentSlot: slotIndex }));
        }
      }
    };

    const handleGlobalMouseUp = async () => {
      if (!dragState) return;

      const { type } = dragState;

      try {
        if (type === 'create') {
          const startSlot = Math.min(dragState.startSlot, dragState.endSlot);
          const endSlot = Math.max(dragState.startSlot, dragState.endSlot);

          if (startSlot === endSlot) {
            setDragState(null);
            return;
          }

          const startTime = slotToDateTime(startSlot);
          const endTime = slotToDateTime(endSlot + 1);

          createShift(dragState.employeeId, startTime, endTime);
        } else if (type === 'move') {
          const { shift, initialStartSlot, deltaSlots } = dragState;

          // If no movement recorded or delta is undefined (e.g. just clicked)
          // Default delta is 0
          const actualDelta = deltaSlots || 0;

          if (actualDelta !== 0) {
            // New Start
            const newStartSlot = initialStartSlot + actualDelta;

            // Original Duration
            const oldStartSlot = timeToSlotIndex(shift.start_time, twoDaysBefore);
            const oldEndSlot = timeToSlotIndex(shift.end_time, twoDaysBefore);
            const durationSlots = oldEndSlot - oldStartSlot;

            const newStartTime = slotToDateTime(newStartSlot);
            const newEndTime = slotToDateTime(newStartSlot + durationSlots);

            await updateShift(
              shift.id,
              toLocalISOString(parseDateSafe(newStartTime)),
              toLocalISOString(parseDateSafe(newEndTime)),
              shift.room_id,
              shift.break_duration,
              shift.no_show || false
            );
          }
        } else if (type === 'resize') {
          const { shift, handleType, currentSlot } = dragState;
          const startTime = parseDateSafe(shift.start_time);
          const endTime = parseDateSafe(shift.end_time);

          if (handleType === 'left') {
            const newStartTime = slotToDateTime(currentSlot);
            if (parseDateSafe(newStartTime) < endTime) {
              updateShift(shift.id, newStartTime, toLocalISOString(endTime), shift.room_id, shift.break_duration, shift.no_show || false);
            }
          } else {
            const newEndTime = slotToDateTime(currentSlot + 1);
            if (startTime < parseDateSafe(newEndTime)) {
              updateShift(shift.id, toLocalISOString(startTime), newEndTime, shift.room_id, shift.break_duration, shift.no_show || false);
            }
          }
        }
      } catch (err) {
        console.error("Action handler error", err);
      }

      // Delay clearing drag state so onClick can still read it to prevent modal opening
      setTimeout(() => setDragState(null), 10);
    };

    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragState, twoDaysBefore]);





  // Fetch data
  useEffect(() => {
    if (!token) return;
    fetchEmployees();
    fetchShifts();
    fetchLeaves();
    fetchRequests();
    fetchRooms();
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, token]);

  // Fetch weather data for the 5-day window using Open-Meteo
  useEffect(() => {
    let isCancelled = false;
    const loadWeather = async () => {
      try {
        const startStr = formatDateLocal(twoDaysBefore);
        const endStr = formatDateLocal(twoDaysAfter);

        const result = await fetchWeekWeather(startStr, endStr, weatherLocation);
        if (!isCancelled && result && result.dailyMap) {
          setWeatherData(result.dailyMap);
        }
      } catch (err) {
        console.warn("Erreur chargement météo VueJournee:", err);
      }
    };

    loadWeather();
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, weatherLocation]);

  const fetchRooms = async () => {
    try {
      const res = await fetch(`${ROOMS_API}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setRooms(data);
      } else {
        console.error("Erreur lors de la récupération des rooms");
        setRooms([]);
      }
    } catch (err) {
      console.error(err);
      setRooms([]);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await fetch(`${TAGS_API}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTags(data);
      }
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${USERS_API}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setEmployees(data);
      } else if (res.status === 403) {
        setEmployees([]);
      }
    } catch (err) {
      console.error('VueJournee: Error fetching employees:', err);
    }
  };

  const fetchShifts = async () => {
    try {
      // Fetch shifts for the selected day (plus margin for overlap)
      const startStr = formatDateLocal(previousDate);
      const endStr = formatDateLocal(nextDate);

      const res = await fetch(
        `${SHIFTS_API}/?start_date=${startStr}&end_date=${endStr}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setShifts(data);

        if (data.length > 0) {
          setEmployees(prevEmployees => {
            const userMap = new Map();
            prevEmployees.forEach(emp => userMap.set(emp.id, emp));
            data.forEach(shift => {
              if (shift.user_id && !userMap.has(shift.user_id) && isShiftInOpenWindow(shift)) {
                userMap.set(shift.user_id, {
                  id: shift.user_id,
                  email: shift.user_email || `User ${shift.user_id}`,
                  full_name: shift.user_name || null,
                  role: 'EMPLOYEE',
                  is_active: false
                });
              }
            });
            return Array.from(userMap.values());
          });
        }
      }
    } catch (err) {
      console.error('VueJournee: Error fetching shifts:', err);
    }
  };

  const fetchLeaves = async () => {
    try {
      const res = await fetch(`${LEAVES_API}/team-requests?status_filter=approved`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLeaves(data);
      }
    } catch (err) {
      console.error('Error fetching leaves:', err);
    }
  };

  const fetchRequests = async () => {
    try {
      const res = await fetch(`${REQUESTS_API}/?status=pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRequests(data);
      }
    } catch (err) {
      console.error('Error fetching requests:', err);
    }
  };

  const LEAVE_TYPE_CFG = {
    paid_leave: { label: 'Congés payés', bg: 'bg-indigo-100/60', border: 'border-indigo-200', text: 'text-indigo-500', badge: 'border-indigo-400 bg-white/50' },
    sick_leave: { label: 'Arrêt maladie', bg: 'bg-orange-100/60', border: 'border-orange-200', text: 'text-orange-500', badge: 'border-orange-400 bg-white/50' },
    work_accident: { label: 'Accident du travail', bg: 'bg-red-100/60', border: 'border-red-200', text: 'text-red-500', badge: 'border-red-400 bg-white/50' },
    unjustified: { label: 'Absence injustifiée', bg: 'bg-gray-100/60', border: 'border-gray-200', text: 'text-gray-500', badge: 'border-gray-400 bg-white/50' },
    compensatory: { label: 'Repos compensateur', bg: 'bg-purple-100/60', border: 'border-purple-200', text: 'text-purple-500', badge: 'border-purple-400 bg-white/50' },
  };

  // Check if employee is on leave for a specific date — returns the leave object or undefined
  const isEmployeeOnLeave = (employeeId, dateObj) => {
    const dateStr = formatDateLocal(dateObj);
    return leaves.find(leave => {
      if (leave.user_id !== employeeId) return false;
      const startDate = toCalendarDateKey(leave.start_datetime || leave.start_date);
      const endDate   = toCalendarDateKey(leave.end_datetime || leave.end_date);
      if (dateStr < startDate) return false;
      const isReturnConvention = leave.end_datetime && /T00:00:00/.test(leave.end_datetime);
      return isReturnConvention ? dateStr < endDate : dateStr <= endDate;
    });
  };

  // Persist selectedRoomId to localStorage
  useEffect(() => {
    if (selectedRoomId !== null) {
      localStorage.setItem('selectedRoomId', selectedRoomId.toString());
    } else {
      localStorage.removeItem('selectedRoomId');
    }
  }, [selectedRoomId]);

  // Filter shifts by selected room (show selected room + unassigned shifts)
  const getFilteredShiftsByRoom = (shiftsArray) => {
    if (selectedRoomId === null || selectedRoomId === undefined) return shiftsArray;
    return shiftsArray.filter(shift =>
      shift.room_id === selectedRoomId || shift.room_id === null || shift.room_id === undefined
    );
  };

  // Get shifts for employee on a specific date/time
  const getShiftsForEmployee = (employeeId) => {
    return getFilteredShiftsByRoom(shifts.filter(shift => shift.user_id === employeeId));
  };

  // Convert time string to slot index (0-239 for 5 days)
  const timeToSlotIndex = (timeStr, baseDate) => {
    const shiftDate = parseDateSafe(timeStr);
    const baseDateOnly = new Date(baseDate);
    baseDateOnly.setHours(0, 0, 0, 0);

    // Calculate day offset from twoDaysBefore
    const startDateOnly = new Date(twoDaysBefore);
    startDateOnly.setHours(0, 0, 0, 0);

    const dayOffset = getCalendarDayOffset(shiftDate, startDateOnly);

    const hours = shiftDate.getHours();
    const minutes = shiftDate.getMinutes();
    const slotInDay = hours * 2 + (minutes >= 30 ? 1 : 0);

    return dayOffset * 48 + slotInDay;
  };

  // Create shift via API
  const createShift = async (employeeId, startTime, endTime) => {
    try {
      // Append slash to avoid 307 redirect issues with some browsers/servers
      const res = await fetch(`${SHIFTS_API}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: employeeId,
          start_time: startTime,
          end_time: endTime,
          room_id: selectedRoomId || null,
          validated: false
        })
      });

      if (res.ok) {
        await fetchShifts();
      } else {
        const err = await res.json();
        alert(`Erreur: ${err.detail || 'Impossible de créer le shift'}`);
      }
    } catch (err) {
      console.error('Error creating shift:', err);
      alert("Erreur réseau lors de la création du shift");
    }
  };

  // Update shift via API
  const updateShift = async (shiftId, startTime, endTime, roomId = null, breakDuration = 0, noShow = false, comment = "", userId = null, tagIds = null) => {
    try {
      const body = {
        start_time: startTime,
        end_time: endTime,
        room_id: roomId,
        break_duration: breakDuration,
        no_show: noShow,
        comment: comment
      };
      if (userId) body.user_id = userId;
      if (tagIds !== null) body.tag_ids = tagIds;

      const res = await fetch(`${SHIFTS_API}/${shiftId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        await fetchShifts();
      } else {
        const err = await res.json();
        alert(`Erreur: ${err.detail || 'Impossible de modifier le shift'}`);
      }
    } catch (err) {
      console.error('Error updating shift:', err);
    }
  };

  // Delete shift via API
  const deleteShift = async (shiftId) => {
    if (!window.confirm('Supprimer ce shift ?')) return;

    try {
      const res = await fetch(`${SHIFTS_API}/${shiftId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        await fetchShifts();
      }
    } catch (err) {
      console.error('Error deleting shift:', err);
    }
  };

  const getPendingRequestForShift = (shiftId) => {
    if (!requests) return null;
    return requests.find(req => req.shift_id === shiftId && req.status === "pending");
  };

  const handleSaveEditShift = async (e) => {
    e.preventDefault();
    const { shift } = shiftActionModal;
    if (!shift) return;

    // Check if start time is before end time
    const start = parseDateSafe(editShift.start_time);
    const end = parseDateSafe(editShift.end_time);
    if (end <= start) {
      alert("L'heure de fin doit être postérieure à l'heure de début.");
      return;
    }

    try {
      const newUserId = editShift.user_id && editShift.user_id !== String(shift.user_id)
        ? Number(editShift.user_id)
        : null;

      await updateShift(
        shift.id,
        editShift.start_time,
        editShift.end_time,
        editShift.room_id || null,
        Number(editShift.break_duration) || 0,
        editShift.no_show,
        editShift.comment,
        newUserId,
        selectedTagIds
      );

      setShiftActionModal({ show: false, shift: null, mode: null });
      fetchShifts();
    } catch (err) {
      console.error("Error saving edit shift:", err);
      alert("Erreur lors de l'enregistrement des modifications");
    }
  };

  const handleDeleteSingleShift = async (shiftId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce shift ?")) return;

    try {
      const response = await fetch(`${SHIFTS_API}/${shiftId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Erreur lors de la suppression");

      setShiftActionModal({ show: false, shift: null, mode: null });
      fetchShifts();
    } catch (err) {
      console.error(err);
      alert("Impossible de supprimer ce shift");
    }
  };

  const handleValidateSingleShift = async (shiftId) => {
    try {
      const response = await fetch(`${SHIFTS_API}/${shiftId}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          validated: true,
          no_show: false // Explicitly set no_show to false when validating normally
        }),
      });

      if (!response.ok) throw new Error("Erreur lors de la validation");

      setShiftActionModal(prev => ({
        ...prev,
        shift: { ...prev.shift, validated: true, no_show: false }
      }));
      fetchShifts();
    } catch (err) {
      console.error(err);
      alert("Impossible de valider ce shift");
    }
  };

  const handleModificationRequest = async (e) => {
    e.preventDefault();
    const { shift } = shiftActionModal;
    if (!shift) return;

    if (requestModification.end_time <= requestModification.start_time) {
      alert("L'heure de fin doit être postérieure à l'heure de début");
      return;
    }

    const originalStart = shift.start_time?.slice(0, 16) || "";
    const originalEnd = shift.end_time?.slice(0, 16) || "";

    if (
      requestModification.start_time === originalStart &&
      requestModification.end_time === originalEnd
    ) {
      alert("Aucune modification n'a été apportée au shift.");
      return;
    }

    try {
      const response = await fetch(`${REQUESTS_API}/modification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          shift_id: shift.id,
          suggested_start: requestModification.start_time,
          suggested_end: requestModification.end_time,
          reason: "Demande de modification d'horaires",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Erreur lors de l'envoi de la demande");
      }

      alert("Demande de modification envoyée au manager.");
      setShiftActionModal({ show: false, shift: null, mode: null });
      fetchRequests();
    } catch (err) {
      console.error(err);
      alert(err.message || "Impossible d'envoyer la demande");
    }
  };

  // Handle mouse down to start dragging/creating
  const handleMouseDown = (e, employeeId, slotIndex, existingShift = null) => {
    if (!isTeamLeaderOrAbove) return;

    // Initialize drag metrics
    dragMetricsRef.current = { moved: false, startX: e.clientX };

    // Prevent default to avoid text selection
    e.preventDefault();
    e.stopPropagation(); // Stop event bubbling

    const target = e.target;
    // Check if we clicked a resize handle
    const isResizeHandle = target.classList.contains('resize-handle');
    const resizeType = target.classList.contains('resize-left') ? 'left' : 'right';

    // Calculate slot width roughly for initial setup if needed, or use fixed
    const slotWidth = 1714 / 48; // ~35.71

    if (existingShift && isResizeHandle) {
      setDragState({
        type: 'resize',
        shift: existingShift,
        handleType: resizeType,
        initialSlot: slotIndex, // Clicked slot (or shift start/end depending on handle)
        startX: e.clientX,
        currentX: e.clientX,
        employeeId
      });
    } else if (existingShift) {
      // Move mode - Delta approach
      // We store the initial shift start slot and the mouse X.
      const shiftStartSlot = timeToSlotIndex(existingShift.start_time, twoDaysBefore);

      setDragState({
        type: 'move',
        shift: existingShift,
        initialStartSlot: shiftStartSlot,
        startX: e.clientX,
        currentX: e.clientX,
        employeeId,
        slotWidth: slotWidth
      });
    } else {
      // Create mode
      const dateForSlot = getDateForSlot(slotIndex);
      if (isEmployeeOnLeave(employeeId, dateForSlot)) {
        alert('Impossible de créer un shift pendant un congé');
        return;
      }

      setDragState({
        type: 'create',
        employeeId,
        startSlot: slotIndex,
        endSlot: slotIndex,
        startX: e.clientX, // Store validation or delta if needed
        slotWidth: slotWidth
      });
    }
  };



  // Convert slot index to datetime (0-239 for 5 days)
  const slotToDateTime = (slotIndex) => {
    const dayOffset = Math.floor(slotIndex / 48);
    const slotInDay = slotIndex % 48;

    const baseDate = new Date(twoDaysBefore);
    baseDate.setDate(baseDate.getDate() + dayOffset);
    baseDate.setHours(0, 0, 0, 0);

    const hours = Math.floor(slotInDay / 2);
    const minutes = (slotInDay % 2) * 30;

    baseDate.setHours(hours, minutes);
    // Return local ISO string manually to ensure consistency and avoid timezones
    return toLocalISOString(baseDate);
  };

  // Get date for slot index (0-239 for 5 days)
  const getDateForSlot = (slotIndex) => {
    const dayOffset = Math.floor(slotIndex / 48);
    const date = new Date(twoDaysBefore);
    date.setDate(date.getDate() + dayOffset);
    return date;
  };

  // Navigate to different day
  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
    navigate(`/gestionShifts/jour/${formatDateLocal(newDate)}`);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
    navigate(`/gestionShifts/jour/${formatDateLocal(newDate)}`);
  };

  // Format date for display
    const formatDisplayDate = (dateObj) => {
    const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    return dateObj.toLocaleDateString('fr-FR', options);
  };

  // Get employee color
  const getEmployeeColor = (index) => {
    const colors = [
      { gradient: 'from-blue-500 to-indigo-600', bg: 'bg-blue-100', border: 'border-blue-300' },
      { gradient: 'from-green-500 to-emerald-600', bg: 'bg-green-100', border: 'border-green-300' },
      { gradient: 'from-purple-500 to-pink-600', bg: 'bg-purple-100', border: 'border-purple-300' },
      { gradient: 'from-orange-500 to-red-600', bg: 'bg-orange-100', border: 'border-orange-300' },
      { gradient: 'from-teal-500 to-cyan-600', bg: 'bg-teal-100', border: 'border-teal-300' },
    ];
    return colors[index % colors.length];
  };

  // Render shift block for continuous timeline
  const renderShift = (shift, employeeIndex) => {
    const startSlot = timeToSlotIndex(shift.start_time, twoDaysBefore);
    const endSlot = timeToSlotIndex(shift.end_time, twoDaysBefore);

    // Filter out shifts that are completely outside the 5-day window
    if (startSlot < 0 || startSlot >= 240 || endSlot < 0 || endSlot > 240) {
      return null;
    }

    // Also check by date to be extra sure
    const shiftStart = parseDateSafe(shift.start_time);
    const shiftEnd = parseDateSafe(shift.end_time);
    const windowStart = new Date(twoDaysBefore);
    windowStart.setHours(0, 0, 0, 0);
    const windowEnd = new Date(twoDaysAfter);
    windowEnd.setHours(23, 59, 59, 999);

    // Skip shifts that don't overlap with our 5-day window
    if (shiftEnd <= windowStart || shiftStart > windowEnd) {
      return null;
    }

    const duration = endSlot - startSlot;

    // Check if being dragged
    const isBeingMoved = dragState?.type === 'move' && dragState.shift.id === shift.id;
    const isBeingResized = dragState?.type === 'resize' && dragState.shift.id === shift.id;

    let displayStartSlot = startSlot;
    let displayDuration = duration;

    if (isBeingMoved) {
      // Delta mode
      const delta = dragState.deltaSlots || 0;
      displayStartSlot = dragState.initialStartSlot + delta;
    } else if (isBeingResized) {
      if (dragState.handleType === 'left') {
        displayStartSlot = dragState.currentSlot;
        displayDuration = endSlot - dragState.currentSlot;
      } else {
        displayDuration = (dragState.currentSlot + 1) - startSlot;
      }
    }

    // ── Couleur par étiquette (même logique que GestionShifts mobile) ──
    const primaryTag = shift.tags && shift.tags.length > 0 ? shift.tags[0] : null;
    const additionalTags = shift.tags && shift.tags.length > 1 ? shift.tags.slice(1) : [];
    const tagColor = primaryTag ? primaryTag.color : null;

    // Style inline pour les couleurs dynamiques
    const shiftStyle = shift.no_show
      ? {
        backgroundColor: '#FEE2E2', // bg-red-100
        borderColor: '#EF4444',     // border-red-500
        color: '#B91C1C',           // text-red-700
      }
      : tagColor
        ? {
          backgroundColor: `${tagColor}20`,   // ~12% opacity
          borderColor: tagColor,
          color: tagColor,
        }
        : {
          backgroundColor: '#F3F4F6',          // gris clair (même que GestionShifts)
          borderColor: '#9CA3AF',
          color: '#6B7280',
        };

    return (
      <div
        key={shift.id}
        className={`group absolute inset-y-0 hover:inset-y-0 rounded-lg shadow-md cursor-move border-2 flex items-center justify-center text-xs font-semibold hover:shadow-xl transition-all ${isBeingMoved ? 'opacity-70 z-30' : 'hover:z-20'
          } ${shift.no_show ? 'opacity-50' : ''}`}
        style={{
          left: `${(displayStartSlot / 240) * 100}%`,
          width: `${(displayDuration / 240) * 100}%`,
          minWidth: '20px',
          zIndex: isBeingMoved ? 30 : 10,
          ...shiftStyle,
        }}
        onMouseDown={(e) => handleMouseDown(e, shift.user_id, displayStartSlot, shift)}
        onClick={(e) => {
          e.stopPropagation();
          // Ignorer le clic si un drag/resize vient d'avoir lieu (> 5px de mouvement)
          if (dragMetricsRef.current.moved) {
            return;
          }

          setEditShift({
            user_id: shift.user_id ? String(shift.user_id) : "",
            start_time: shift.start_time?.slice(0, 16) || "",
            end_time: shift.end_time?.slice(0, 16) || "",
            no_show: shift.no_show || false,
            room_id: shift.room_id || "",
            break_duration: shift.break_duration || 0,
            comment: shift.comment || "",
          });
          setSelectedTagIds(shift.tags ? shift.tags.map(t => t.id) : []);
          setRequestModification({
            start_time: shift.start_time?.slice(0, 16) || "",
            end_time: shift.end_time?.slice(0, 16) || "",
          });
          setShiftActionModal({
            show: true,
            shift,
            mode: isTeamLeaderOrAbove ? 'edit' : 'request'
          });
        }}
      >
        <div
          className="resize-handle resize-left absolute left-0 top-0 bottom-0 w-3 bg-black bg-opacity-0 hover:bg-opacity-10 cursor-ew-resize rounded-l-lg"
          style={{ zIndex: 20 }}
          onMouseDown={(e) => {
            e.stopPropagation();
            handleMouseDown(e, shift.user_id, displayStartSlot, shift);
          }}
        />



        {/* Badges étiquettes supplémentaires */}
        {additionalTags.length > 0 && (
          <div className="absolute bottom-1 left-2 flex gap-0.5 pointer-events-none">
            {additionalTags.map(tag => (
              <div
                key={tag.id}
                className="w-2 h-2 rounded-full border border-white/70 shadow-sm"
                style={{ backgroundColor: tag.color }}
                title={tag.name}
              />
            ))}
          </div>
        )}

        <div className="px-2 truncate pointer-events-none select-none font-semibold flex items-center gap-1.5">
          {/* Pastille couleur si étiquette */}
          {primaryTag && (
            <div
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: tagColor }}
            />
          )}
          {/* Badge validé */}
          {shift.validated && (
            <span
              className="text-[9px] font-bold px-1 py-0.5 rounded leading-none flex-shrink-0"
              style={{ backgroundColor: tagColor || '#9CA3AF', color: '#fff', opacity: 0.9 }}
            >
              ✓
            </span>
          )}
          {shift.no_show && (
            <div className="flex items-center gap-1 bg-red-100 px-1 py-0.5 rounded text-[10px] font-bold text-red-600 border border-red-200 shadow-sm flex-shrink-0">
              <X className="w-2.5 h-2.5" />
              <span>ABSENT</span>
            </div>
          )}
          <span className={shift.no_show ? 'line-through opacity-70' : ''}>
            {formatTimeSafe(shiftStart, { hour: '2-digit', minute: '2-digit' })}
            {' – '}
            {formatTimeSafe(shiftEnd, { hour: '2-digit', minute: '2-digit' })}
          </span>
          <span className={`ml-1 font-bold opacity-90 ${shift.no_show ? 'line-through opacity-70' : ''}`}>
            {(() => {
              const start = parseDateSafe(shiftStart);
              const end = parseDateSafe(shiftEnd);
              const durationMin = Math.round((end - start) / (1000 * 60));
              const breakDur = Number(shift.break_duration) || 0;
              const netDuration = Math.max(0, durationMin - breakDur);
              const h = Math.floor(netDuration / 60);
              const m = netDuration % 60;
              return `· ${h}h${m > 0 ? m : ''}`;
            })()}
          </span>
          {Number(shift.break_duration) > 0 && (
            <span className="text-[10px] font-medium opacity-80 flex-shrink-0 ml-1">
              (pause {shift.break_duration}m)
            </span>
          )}
          {shift.comment && (
            <div className="flex-shrink-0 flex items-center text-gray-500 ml-1" title={shift.comment}>
              <MessageSquare className="w-3 h-3" />
            </div>
          )}
          {/* Nom étiquette si shift assez large */}
          {primaryTag && (
            <span className="text-[9px] font-medium opacity-80 flex-shrink-0 hidden group-hover:inline">
              · {primaryTag.name}
            </span>
          )}
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            deleteShift(shift.id);
          }}
          className="absolute right-1 top-1 p-1 bg-red-500 text-white rounded-md hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
          style={{ zIndex: 30 }}
        >
          <Trash2 size={10} />
        </button>

        <div
          className="resize-handle resize-right absolute right-0 top-0 bottom-0 w-3 bg-black bg-opacity-0 hover:bg-opacity-10 cursor-ew-resize rounded-r-lg"
          style={{ zIndex: 20 }}
          onMouseDown={(e) => {
            e.stopPropagation();
            handleMouseDown(e, shift.user_id, displayStartSlot, shift);
          }}
        />
      </div>
    );
  };

  // Render preview for drag
  const renderDragPreview = () => {
    if (!dragState || dragState.type !== 'create') return null;

    const startSlot = Math.min(dragState.startSlot, dragState.endSlot);
    const endSlot = Math.max(dragState.startSlot, dragState.endSlot);
    const duration = endSlot - startSlot + 1;

    return (
      <div
        className="absolute inset-y-0 bg-indigo-500 bg-opacity-30 border-2 border-indigo-400 border-dashed rounded-lg z-20 pointer-events-none"
        style={{
          left: `${(startSlot / 240) * 100}%`,
          width: `${(duration / 240) * 100}%`
        }}
      />
    );
  };

  // Reorder Modal Logic
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [tempOrder, setTempOrder] = useState([]);

  const handleOpenOrderModal = () => {
    setTempOrder(sortedUsers.map(u => u.id));
    setShowOrderModal(true);
  };

  const moveUser = (index, direction) => {
    const newOrder = [...tempOrder];
    if (direction === 'up' && index > 0) {
      [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
    } else if (direction === 'down' && index < newOrder.length - 1) {
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    }
    setTempOrder(newOrder);
  };

  // Drag and Drop Sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setTempOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const saveOrder = () => {
    setUsersOrder(tempOrder);
    localStorage.setItem('usersOrder', JSON.stringify(tempOrder));
    setShowOrderModal(false);
  };

  // Scroll container ref
  const scrollContainerRef = useRef(null);

  // Auto-scroll to center on 7h (work start time)
  useEffect(() => {
    if (scrollContainerRef.current) {
      const dayWidth = 1714;
      const centerDayOffset = dayWidth * 2; // Skip first 2 days to get to center day
      const hourOffset = 14 * 35.71; // 7h = slot 14
      scrollContainerRef.current.scrollLeft = centerDayOffset + hourOffset - 400; // -400 to center in viewport
    }
  }, [selectedDate]);

  // Calculate employee counts per slot
  const slotCounts = React.useMemo(() => {
    const counts = new Array(240).fill(0);
    shifts.forEach(shift => {
      const startSlot = timeToSlotIndex(shift.start_time, twoDaysBefore);
      const endSlot = timeToSlotIndex(shift.end_time, twoDaysBefore);
      const realStart = Math.max(0, startSlot);
      const realEnd = Math.min(239, endSlot);
      for (let i = realStart; i < realEnd; i++) {
        counts[i]++;
      }
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shifts, twoDaysBefore]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Shift Action Modal */}
      {shiftActionModal.show && shiftActionModal.shift && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col transform transition-all scale-100 overflow-hidden">
            {/* Modal Header */}
            <div className="p-4 sm:p-6 border-b border-gray-100 bg-gray-50 flex justify-between items-center flex-shrink-0">
              <h2 className="text-base sm:text-xl font-bold text-gray-800">
                {shiftActionModal.mode === "edit" ? "Modifier le shift" : shiftActionModal.mode === "view-request" ? "Détails de la demande" : "Demander une modification"}
              </h2>
              <button
                onClick={() => setShiftActionModal({ show: false, shift: null, mode: null })}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Fermer"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors">✕</div>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
              {/* Employee Info */}
              <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className={`w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center text-white font-semibold shadow-md`}>
                  {(shiftActionModal.shift.user_name || shiftActionModal.shift.user_email || "?").charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-bold text-gray-900 text-lg">
                    {shiftActionModal.shift.user_name || shiftActionModal.shift.user_email || `User ${shiftActionModal.shift.user_id}`}
                  </div>
                  <div className="text-sm text-gray-500 flex items-center gap-2">
                    {formatDateSafe(shiftActionModal.shift.start_time, {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long'
                    })}
                  </div>
                </div>
              </div>


              {/* Edit Mode (Manager) */}
              {shiftActionModal.mode === "edit" && (() => {
                const pendingRequest = getPendingRequestForShift(shiftActionModal.shift.id);
                return (
                  <form onSubmit={handleSaveEditShift} className="space-y-5">

                    {/* Display pending request if exists */}
                    {pendingRequest && (
                      <div className="p-4 bg-orange-50 border-2 border-orange-200 rounded-xl">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <h4 className="font-semibold text-orange-900 mb-1">
                              Une demande de modification est en attente
                            </h4>
                            <div className="text-sm text-orange-800 mb-2">
                              L'employé a demandé de modifier les horaires de ce shift.
                            </div>
                            <button
                              type="button"
                              onClick={() => setShiftActionModal({
                                ...shiftActionModal,
                                mode: "view-request",
                                request: pendingRequest
                              })}
                              className="px-4 py-2 bg-white text-orange-700 font-medium rounded-lg border border-orange-200 hover:bg-orange-100 transition-colors text-sm"
                            >
                              Voir la demande
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Employé (transfert de shift) */}
                    <div className="mb-1">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Employé
                      </label>
                      <select
                        value={editShift.user_id || ""}
                        onChange={(e) => setEditShift({ ...editShift, user_id: e.target.value })}
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                        required
                      >
                        <option value="">— Sélectionner un employé —</option>
                        {sortedUsers.map((emp) => (
                          <option key={emp.id} value={String(emp.id)} className={!isUserActive(emp) ? 'line-through text-gray-400 italic' : ''}>
                            {emp.full_name || emp.email}{!isUserActive(emp) ? ' (Inactif)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Heure de début
                        </label>
                        <input
                          type="datetime-local"
                          value={editShift.start_time}
                          onChange={(e) => setEditShift({ ...editShift, start_time: e.target.value })}
                          className="w-full px-3 sm:px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all min-w-0 max-w-full box-border text-sm sm:text-base"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Heure de fin
                        </label>
                        <input
                          type="datetime-local"
                          value={editShift.end_time}
                          onChange={(e) => setEditShift({ ...editShift, end_time: e.target.value })}
                          className="w-full px-3 sm:px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all min-w-0 max-w-full box-border text-sm sm:text-base"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                      {/* Note: This assumes rooms state is available if needed, else we keep the ID input */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Salle (Optionnel)
                        </label>
                        <select
                          value={editShift.room_id || ""}
                          onChange={(e) => setEditShift({ ...editShift, room_id: e.target.value === "" ? "" : parseInt(e.target.value) })}
                          className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                        >
                          <option value="">Aucune salle</option>
                          {rooms.map((room) => (
                            <option key={room.id} value={room.id}>
                              {room.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Temps de pause (min)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="5"
                          value={editShift.break_duration || 0}
                          onChange={(e) => setEditShift({ ...editShift, break_duration: parseInt(e.target.value) || 0 })}
                          className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Commentaire (Optionnel)
                      </label>
                      <textarea
                        value={editShift.comment || ""}
                        onChange={(e) => setEditShift({ ...editShift, comment: e.target.value })}
                        rows="2"
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
                        placeholder="Ex: Remplacer à 14h, ou besoin de renfort..."
                      ></textarea>
                    </div>

                    <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                          <input
                            type="checkbox"
                            role="switch"
                            id="no_show_toggle"
                            checked={editShift.no_show}
                            onChange={(e) => setEditShift({ ...editShift, no_show: e.target.checked })}
                            className="w-4 h-4 text-orange-600 rounded cursor-pointer"
                          />
                          <label htmlFor="no_show_toggle" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                            Absent
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Étiquettes */}
                    {tags.length > 0 && (
                      <div className="mt-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Étiquettes (optionnel)
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {tags.map((tag) => (
                            <button
                              key={tag.id}
                              type="button"
                              onClick={() => {
                                setSelectedTagIds(prev =>
                                  prev.includes(tag.id)
                                    ? prev.filter(id => id !== tag.id)
                                    : [...prev, tag.id]
                                );
                              }}
                              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                selectedTagIds.includes(tag.id)
                                  ? 'ring-2 ring-offset-2 ring-indigo-500 scale-105'
                                  : 'hover:opacity-80'
                              }`}
                              style={{
                                backgroundColor: tag.color,
                                color: '#ffffff',
                              }}
                            >
                              {selectedTagIds.includes(tag.id) && (
                                <Check className="w-3 h-3 inline mr-1" />
                              )}
                              {tag.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => handleDeleteSingleShift(shiftActionModal.shift.id)}
                        className="p-2.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors"
                        title="Supprimer le shift"
                        disabled={editShift.no_show}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                      <button
                        type="submit"
                        className="flex-1 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-xl shadow-md hover:shadow-lg transition-all"
                      >
                        Enregistrer
                      </button>
                    </div>

                    {/* Validation Button (if not validated & not no_show & in past or current) */}
                    {(() => {
                      const shiftEnd = parseDateSafe(shiftActionModal.shift.end_time);
                      const now = new Date();
                      const isPastOrCurrent = now >= shiftEnd || (now >= parseDateSafe(shiftActionModal.shift.start_time) && now < shiftEnd);
                      const isValidated = shiftActionModal.shift.validated;

                      if (!isValidated && !editShift.no_show && isPastOrCurrent) {
                        return (
                          <div className="pt-4 border-t border-gray-100">
                            <button
                              type="button"
                              onClick={() => {
                                handleValidateSingleShift(shiftActionModal.shift.id);
                              }}
                              className="w-full flex justify-center items-center gap-2 px-5 py-2.5 bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 font-medium rounded-xl transition-all"
                            >
                              <Check className="w-5 h-5" />
                              Valider ce shift (Réalisé)
                            </button>
                            <p className="text-xs text-center text-gray-500 mt-2">
                              Validez ce shift pour confirmer qu'il a bien été effectué par l'employé.
                            </p>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </form>
                );
              })()}

              {/* Request Mode (Employee) */}
              {shiftActionModal.mode === "request" && (
                <form onSubmit={handleModificationRequest} className="space-y-5">
                  <div className="p-4 bg-indigo-50 border-2 border-indigo-100 rounded-xl text-indigo-800 text-sm mb-6">
                    Vous souhaitez modifier l'horaire de ce shift ? Soumettez une demande à votre manager ci-dessous.
                  </div>

                  {shiftActionModal.shift.comment && (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 text-sm mb-4 flex items-start gap-2">
                      <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-gray-600 block mb-1">Commentaire du manager</span>
                        {shiftActionModal.shift.comment}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Heure de début souhaitée
                      </label>
                      <input
                        type="datetime-local"
                        value={requestModification.start_time}
                        onChange={(e) => setRequestModification({ ...requestModification, start_time: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all min-w-0 max-w-full box-border text-sm sm:text-base"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Heure de fin souhaitée
                      </label>
                      <input
                        type="datetime-local"
                        value={requestModification.end_time}
                        onChange={(e) => setRequestModification({ ...requestModification, end_time: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all min-w-0 max-w-full box-border text-sm sm:text-base"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShiftActionModal({ show: false, shift: null, mode: null })}
                      className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-xl shadow-md hover:shadow-lg transition-all text-center flex justify-center items-center gap-2"
                    >
                      <span>Envoyer la demande</span>
                    </button>
                  </div>
                </form>
              )}

              {/* View Request Mode (Manager views an employee's request) */}
              {shiftActionModal.mode === "view-request" && shiftActionModal.request && (
                <div className="space-y-6">
                  {/* Status Banner */}
                  <div className={`p-4 rounded-xl border-2 flex items-center justify-between ${shiftActionModal.request.status === 'pending'
                      ? 'bg-orange-50 border-orange-200'
                      : shiftActionModal.request.status === 'approved'
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}>
                    <div className={`font-bold uppercase tracking-wider text-sm ${shiftActionModal.request.status === 'pending' ? 'text-orange-700' :
                        shiftActionModal.request.status === 'approved' ? 'text-green-700' : 'text-red-700'
                      }`}>
                      {shiftActionModal.request.status === 'pending' && 'En attente'}
                      {shiftActionModal.request.status === 'approved' && 'Approuvée'}
                      {shiftActionModal.request.status === 'rejected' && 'Rejetée'}
                    </div>
                  </div>

                  {shiftActionModal.shift.comment && (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 text-sm mb-4 flex items-start gap-2">
                      <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-gray-600 block mb-1">Commentaire du manager</span>
                        {shiftActionModal.shift.comment}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 bg-gray-50 p-4 sm:p-5 rounded-xl border border-gray-100">
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Horaires Actuels</h4>
                      <div className="flex items-center gap-2 text-gray-700 font-medium bg-white px-3 py-2 rounded-lg border border-gray-200">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span>{shiftActionModal.shift.start_time?.slice(11, 16) || '??:??'}</span>
                        <span className="text-gray-400">→</span>
                        <span>{shiftActionModal.shift.end_time?.slice(11, 16) || '??:??'}</span>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Horaires Souhaités</h4>
                      <div className="flex items-center gap-2 text-indigo-700 font-medium bg-indigo-50 px-3 py-2 rounded-lg border border-indigo-100">
                        <Clock className="w-4 h-4 text-indigo-400" />
                        <span>{shiftActionModal.request.start_time?.slice(11, 16) || '??:??'}</span>
                        <span className="text-indigo-400">→</span>
                        <span>{shiftActionModal.request.end_time?.slice(11, 16) || '??:??'}</span>
                      </div>
                    </div>
                  </div>

                  {shiftActionModal.request.reason && (
                    <div className="mb-4">
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Message de l'employé</h4>
                      <p className="text-sm text-gray-600 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">{shiftActionModal.request.reason}</p>
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                    <span className="text-xs text-gray-400">
                      Demandé le {formatDateSafe(shiftActionModal.request.created_at, {
                        day: 'numeric',
                        month: 'long',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    <button
                      onClick={() => {
                        // Switch back to edit mode to see the normal shift form
                        setEditShift({
                          start_time: shiftActionModal.request.start_time || shiftActionModal.shift.start_time,
                          end_time: shiftActionModal.request.end_time || shiftActionModal.shift.end_time,
                          no_show: shiftActionModal.shift.no_show,
                          room_id: shiftActionModal.shift.room_id || "",
                          break_duration: shiftActionModal.shift.break_duration || 0,
                        });
                        setShiftActionModal({ ...shiftActionModal, mode: "edit" });
                      }}
                      className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                    >
                      Retour à l'édition
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reorder Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col transform transition-all scale-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h2 className="text-xl font-bold text-gray-800">Réorganiser l'équipe</h2>
              <button
                onClick={() => setShowOrderModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors">✕</div>
              </button>
            </div>

            <div className="p-4 max-h-[60vh] overflow-y-auto">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={tempOrder}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {tempOrder.map((userId, index) => {
                      const user = employees.find(u => u.id === userId);
                      if (!user) return null;
                      const colorMatch = getEmployeeColor(index);
                      return (
                        <SortableUserItem
                          key={userId}
                          id={userId}
                          user={user}
                          index={index}
                          moveUser={moveUser}
                          isFirst={index === 0}
                          isLast={index === tempOrder.length - 1}
                          colorMatch={colorMatch}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
            <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
              <button onClick={() => setShowOrderModal(false)} className="px-5 py-2.5 rounded-xl text-gray-700 font-medium hover:bg-gray-200 transition-colors">Annuler</button>
              <button onClick={saveOrder} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all">Enregistrer l'ordre</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/gestionShifts', { state: { date: selectedDate.toISOString() } })}
                className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Vue Journée
              </h1>
            </div>

            <div className="flex items-center bg-gray-100 rounded-xl p-1 shadow-inner">
              <button
                onClick={goToPreviousDay}
                className="p-2 hover:bg-white rounded-lg text-gray-600 transition-all shadow-sm hover:shadow-md"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="px-6 font-semibold text-gray-800 min-w-[180px] text-center">
                {formatDisplayDate(selectedDate)}
              </div>
              <button
                onClick={goToNextDay}
                className="p-2 hover:bg-white rounded-lg text-gray-600 transition-all shadow-sm hover:shadow-md"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {isTeamLeaderOrAbove && (
              <div className="flex items-center gap-2 flex-wrap">
                {/* Filtre par salle */}
                {rooms.length > 0 && (
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                    <select
                      value={selectedRoomId !== null ? selectedRoomId : ""}
                      onChange={(e) => setSelectedRoomId(e.target.value === "" ? null : parseInt(e.target.value))}
                      className={`pl-8 pr-3 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-indigo-500 transition-all shadow-sm font-medium appearance-none cursor-pointer ${
                        selectedRoomId !== null
                          ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                      title="Filtrer par salle"
                    >
                      <option value="">Toutes les salles</option>
                      {rooms.map((room) => (
                        <option key={room.id} value={room.id}>{room.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {/* Bouton tri par heure — local uniquement, non persisté */}
                <button
                  onClick={() => setSortByTime(prev => !prev)}
                  className={`flex items-center gap-2 px-4 py-2 border rounded-xl font-medium text-sm transition-all shadow-sm ${sortByTime
                      ? 'bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                    }`}
                  title={sortByTime ? "Revenir à l'ordre par défaut" : 'Trier par heure de début'}
                >
                  <ArrowUpDown className="w-4 h-4" />
                  {sortByTime ? 'Tri horaire actif' : 'Trier par horaire'}
                </button>
                {/* Bouton réorganiser manuel */}
                <button
                  onClick={handleOpenOrderModal}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm font-medium text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                  Réorganiser
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Continuous Timeline Table */}
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-lg overflow-x-auto" ref={scrollContainerRef} style={{ maxWidth: '100%' }}>
          {/* Day headers - 5 days continuous */}
          <div className="flex flex-col border-b bg-gray-50 sticky z-30" style={{ width: '8820px', maxWidth: '8820px' }}>
            <div className="flex">
              <div className="w-48 flex-shrink-0 border-r p-3 font-semibold bg-gray-100 sticky left-0 z-40 shadow-sm">
                Employé
              </div>
              <div className="flex" style={{ width: '8570px' }}>
                {[twoDaysBefore, oneDayBefore, selectedDate, oneDayAfter, twoDaysAfter].map((dateObj, dayIndex) => (
                  <div key={dayIndex} style={{ width: '1714px' }} ref={dayIndex === 0 ? timelineRef : null}>
                    <div className={`text-center py-2 text-sm font-medium border-b flex items-center justify-center gap-2 ${dayIndex === 2 ? 'bg-indigo-100 font-bold text-gray-900 border-indigo-200' : 'bg-gray-50 text-gray-700'
                      }`}>
                      <span>{formatDisplayDate(dateObj)}</span>
                      {weatherData[formatDateLocal(dateObj)] && (
                        <WeatherBadge
                          dayWeather={weatherData[formatDateLocal(dateObj)]}
                          onClick={handleOpenWeatherModal}
                        />
                      )}
                    </div>

                    {/* Hour markers & Rain probability for this day */}
                    {(() => {
                      const dayKey = formatDateLocal(dateObj);
                      const dayW = weatherData[dayKey];
                      const hourlyList = dayW?.hourly || [];

                      return (
                        <div className="flex text-xs text-gray-500" style={{ width: '1714px' }}>
                          {Array.from({ length: 48 }).map((_, slotIdx) => {
                            const hour = Math.floor(slotIdx / 2);
                            const isHourMark = slotIdx % 2 === 0;
                            const hourData = hourlyList[hour];
                            const prob = hourData?.rainProb ?? null;

                            return (
                              <div
                                key={slotIdx}
                                className={`text-left py-1 pl-1 ${
                                  isHourMark ? 'border-r border-gray-200' : 'border-r border-gray-100'
                                }`}
                                style={{ width: '35.71px', flexShrink: 0 }}
                              >
                                {isHourMark && (
                                  <div
                                    className="flex items-center gap-1 leading-none select-none whitespace-nowrap cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={() => dayW && handleOpenWeatherModal(dayW)}
                                    title={
                                      hourData
                                        ? `${hour}h00 : ${prob}% probabilité de pluie · Temp: ${hourData.temp}°C · Cliquez pour détails`
                                        : `${hour}h00`
                                    }
                                  >
                                    <span className="font-semibold text-gray-700">{hour}h</span>
                                    {prob !== null && prob >= 15 && (
                                      <span
                                        className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-bold ${
                                          prob >= 60
                                            ? 'bg-blue-600 text-white'
                                            : prob >= 35
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-sky-100 text-sky-800'
                                        }`}
                                      >
                                        <span className="text-[9px] leading-none">{prob >= 50 ? '🌧️' : '💧'}</span>
                                        <span>{prob}%</span>
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </div>

            {/* Employee Count Row */}
            <div className="flex border-t border-gray-200 bg-blue-50/50">
              <div className="w-48 flex-shrink-0 border-r p-2 text-xs font-bold text-blue-800 uppercase flex items-center sticky left-0 bg-blue-50 z-40">
                Effectif présent
              </div>
              <div className="flex" style={{ width: '8570px' }}>
                {slotCounts.map((count, slotIndex) => {
                  const isHourMark = slotIndex % 2 === 0;
                  return (
                    <div
                      key={slotIndex}
                      className={`text-center py-1 text-xs font-semibold ${count > 0 ? 'text-blue-700' : 'text-gray-300'
                        } ${isHourMark ? 'border-r border-blue-200' : 'border-r border-blue-100'
                        }`}
                      style={{ width: '35.71px', flexShrink: 0 }}
                    >
                      {count > 0 ? count : '-'}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Employee rows */}
          <div>
            {sortedUsers.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                <p className="text-lg font-medium">Aucun employé trouvé</p>
              </div>
            )}

            {sortedUsers.map((employee, employeeIndex) => {
              const colors = getEmployeeColor(employeeIndex);
              const employeeShifts = getShiftsForEmployee(employee.id);

              return (
                <div key={employee.id} className="flex hover:bg-gray-50 border-b border-gray-100" style={{ width: '8820px', maxWidth: '8820px', height: '48px' }}>
                  {/* Employee info - sticky */}
                  <div className="w-48 flex-shrink-0 border-r border-gray-100 p-2 flex items-center gap-2 sticky left-0 bg-white z-30 shadow-[4px_0_24px_-12px_rgba(0,0,0,0.1)]">
                    <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${colors.gradient} flex items-center justify-center text-white font-bold text-xs shadow-sm`}>
                      {(employee.full_name || employee.email).charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium truncate text-sm ${!isUserActive(employee) ? 'line-through text-gray-400 italic' : 'text-gray-800'}`}>
                        {employee.full_name || employee.email}
                      </div>
                      {employee.role === 'MANAGER' && (
                        <div className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Manager</div>
                      )}
                      {!isUserActive(employee) && (
                        <div className="text-[10px] text-red-500 font-semibold uppercase tracking-wider">Inactif</div>
                      )}
                    </div>
                  </div>

                  {/* Continuous timeline - 240 slots for 5 days */}
                  <div
                    className="flex-1 relative"
                    style={{ height: '48px', width: '8570px', maxWidth: '8570px' }}
                  >
                    {/* Grid Background */}
                    <div className="absolute inset-0 flex pointer-events-none">
                      {Array.from({ length: 240 }).map((_, slotIndex) => {
                        const dateForSlot = getDateForSlot(slotIndex);
                        const isOnLeave = isEmployeeOnLeave(employee.id, dateForSlot);
                        const dayIndex = Math.floor(slotIndex / 48);
                        const isCurrentDay = dayIndex === 2;

                        return (
                          <div
                            key={slotIndex}
                            className={`border-r hover:bg-blue-50 transition-colors ${isOnLeave ? 'bg-red-50/50' : ''
                              } ${isCurrentDay ? 'bg-indigo-50/20' : ''} ${slotIndex % 2 === 1 ? 'border-r-gray-200' : 'border-r-gray-100'
                              }`}
                            style={{ width: '35.71px', flexShrink: 0 }}
                          />
                        );
                      })}
                    </div>

                    {/* Render leave blocks as full-day overlays */}
                    <div className="absolute inset-0 pointer-events-none">
                      {[twoDaysBefore, oneDayBefore, selectedDate, oneDayAfter, twoDaysAfter].map((dateObj, dayIdx) => {
                        const leaveObj = isEmployeeOnLeave(employee.id, dateObj);
                        if (!leaveObj) return null;
                        const cfg = LEAVE_TYPE_CFG[leaveObj.leave_type] || LEAVE_TYPE_CFG['paid_leave'];

                        const dateStr = formatDateLocal(dateObj);
                        let isHalfM = false;
                        let isHalfA = false;

                        if (leaveObj.start_datetime) {
                          const halves = getLeaveHalves(leaveObj, dateStr);
                          isHalfM = halves.isHalfM;
                          isHalfA = halves.isHalfA;
                        }

                        let sSlot = dayIdx * 48;
                        let wSlot = 48;
                        let labelWithHalf = cfg.label;

                        if (isHalfM && !isHalfA) {
                          wSlot = 24;
                          labelWithHalf += ' (Matin)';
                        } else if (isHalfA && !isHalfM) {
                          sSlot += 24;
                          wSlot = 24;
                          labelWithHalf += ' (Après-midi)';
                        }

                        return (
                          <div
                            key={`leave-${dayIdx}`}
                            className={`absolute top-0 bottom-0 ${cfg.bg} border-l border-r ${cfg.border} flex items-center justify-center`}
                            style={{
                              left: `${(sSlot / 240) * 100}%`,
                              width: `${(wSlot / 240) * 100}%`,
                              zIndex: 5
                            }}
                            title={labelWithHalf}
                          >
                            <div className={`text-xs font-semibold ${cfg.text} select-none border-2 ${cfg.badge} px-2 py-1 rounded-lg max-w-[90%] text-center overflow-hidden text-ellipsis whitespace-nowrap`}>
                              {labelWithHalf}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Interaction Layer */}
                    <div className="absolute inset-0 flex z-0">
                      {Array.from({ length: 240 }).map((_, slotIndex) => {
                        const dateForSlot = getDateForSlot(slotIndex);
                        const isOnLeave = isEmployeeOnLeave(employee.id, dateForSlot);
                        return (
                          <div
                            key={slotIndex}
                            className={`cursor-crosshair ${isOnLeave ? 'cursor-not-allowed' : ''}`}
                            style={{ width: '35.71px', flexShrink: 0 }}
                            onMouseDown={(e) => !isOnLeave && handleMouseDown(e, employee.id, slotIndex)}
                          />
                        );
                      })}
                    </div>

                    {/* Render all shifts for this employee */}
                    {employeeShifts.map(shift => renderShift(shift, employeeIndex))}

                    {/* Render drag preview */}
                    {dragState?.type === 'create' && dragState.employeeId === employee.id && renderDragPreview()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <WeatherDetailModal
        open={weatherModalState.show}
        onClose={() => setWeatherModalState({ show: false, dayWeather: null })}
        dayWeather={weatherModalState.dayWeather}
        currentLocation={weatherLocation}
        onLocationChange={(newLoc) => setWeatherLocation(newLoc)}
      />
    </div>
  );
}
